// src/pages/portal/PortalMandates.js — the portal's landing page. Splits
// LIVE mandates into "Your Mandates" (engaged — links through to the fuller
// detail view, PortalMandateDetail) and "All Active Mandates" (browse-only,
// every partner sees every LIVE mandate regardless of engagement). Narrative
// content and pipeline attribution are both engaged-partners-only. The API
// returns every LIVE mandate plus the caller's own ON HOLD and CLOSED ones
// (migrations/065), so there is no Previous Mandates section.
import { useQuery } from '@tanstack/react-query'
import PortalSearch from '../../components/portal/PortalSearch'
import { useNavigate } from 'react-router-dom'
import api from '../../lib/api'
import WorkTypeTags from '../../components/WorkTypeTags'
import MandateTaxonomyTags from '../../components/MandateTaxonomyTags'
import { GP_LOGOS, GP_LOGOS_LIGHT_OVERRIDE } from '../../lib/gpLogos'
import { GP_WEBSITES } from '../../lib/gpWebsites'
import ExternalLinkIcon from '../../components/portal/ExternalLinkIcon'
import { useAuth } from '../../context/AuthContext'
import { usePortalTheme } from '../../context/PortalThemeContext'
import { PORTAL_PAGE_TITLE_STYLE } from '../../lib/portalPageTitleStyle'
import BranchSections from '../../components/BranchSections'

// Section subheadings echo the page title's weight at a smaller size, in
// normal capitalization (all caps dropped 2026-10-20), rather than the plain small-caption
// style used elsewhere for section labels. color is explicit rather than
// inherited -- an inherited color on portal text has broken silently in
// light mode before (the MandateHeader back link), so themed text here
// always sets its own var(--text-...) rather than relying on the cascade.
export const SECTION_HEADING_STYLE = {
  fontSize: 22,
  fontWeight: 700,
  marginBottom: 12,
  color: 'var(--text-primary)',
}

// First sentence only, capped as a safety net for a long/unpunctuated run
// of text — this is a tile blurb, not the full overview (see the Teaser
// tier for that).
function firstSentence(text) {
  if (!text) return null
  const match = text.match(/^.*?[.!?](?=\s|$)/)
  const sentence = match ? match[0] : text
  return sentence.length > 160 ? `${sentence.slice(0, 157).trimEnd()}…` : sentence
}

function MandateTile({ mandate }) {
  const navigate = useNavigate()
  const { theme } = usePortalTheme()
  const logo = (theme === 'light' && GP_LOGOS_LIGHT_OVERRIDE[mandate.mandate_name])
    || GP_LOGOS[mandate.mandate_name]
  const website = GP_WEBSITES[mandate.mandate_name]

  function goToDetail() { navigate(`/portal/mandates/${mandate.mandate_id}`) }

  return (
    <div
      className="card-hover"
      onClick={goToDetail}
      role="link"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goToDetail() } }}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        minHeight: 340,
        background: 'var(--surface)',
        border: '1px solid var(--tile-outline)',   // cream, so each tile stands out in its panel
        // A partner's closed or on-hold mandate stays visible, but muted.
        opacity: STAGE_NOTE[mandate.bd_stage] ? 0.72 : 1,
        borderRadius: '8px',
        padding: '16px 20px',
        overflow: 'hidden',
      }}
    >
      {/* Not a <Link> -- the whole card navigates via the div's onClick
          above (card-hover's cursor: pointer already signals that), so a
          click anywhere in the tile's empty space works, not just on this
          text block. The two controls below (Add Names, website) stop
          propagation so they don't also trigger the card's own navigation. */}
      <div>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 34 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 19, color: 'var(--text-primary)', marginBottom: 6,
              display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {mandate.mandate_name}
              {STAGE_NOTE[mandate.bd_stage] && (
                <span className="badge badge-gray" style={{ fontSize: 11, fontWeight: 600 }}>
                  {STAGE_NOTE[mandate.bd_stage]}
                </span>
              )}
            </div>
            <div style={{ fontSize: 15, color: 'var(--text-secondary)' }}>
              {mandate.gp_firm_name ?? '—'}
            </div>
          </div>
          {logo && (
            <img
              src={logo}
              alt=""
              style={{
                maxWidth: 140,
                maxHeight: 52,
                objectFit: 'contain',
                flexShrink: 0,
              }}
            />
          )}
        </div>
        {mandate.structure_sector && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginLeft: -8 }}>
            <span
              className="badge badge-gray"
              style={{ fontSize: 13, whiteSpace: 'normal', textAlign: 'left', lineHeight: 1.4 }}
            >
              {mandate.structure_sector}
            </span>
          </div>
        )}
        <WorkTypeTags workType={mandate.work_type} workSubtype={mandate.work_subtype} compact style={{ marginTop: 10 }} />
        <MandateTaxonomyTags tags={mandate.taxonomy_tags} compact style={{ marginTop: 6 }} />
        {firstSentence(mandate.opportunity_overview) && (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, marginTop: 10 }}>
            {firstSentence(mandate.opportunity_overview)}
          </div>
        )}
        {mandate.capital_need && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, marginTop: 8 }}>
            <strong style={{ color: 'var(--text-secondary)' }}>Capital Need: </strong>
            {mandate.capital_need}
          </div>
        )}
        <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, marginTop: 4 }}>
          <strong style={{ color: 'var(--text-secondary)' }}>Target Close: </strong>
          {mandate.target_close || 'TBD'}
        </div>
      </div>

      {website && (
        <a
          href={website}
          target="_blank"
          rel="noreferrer noopener"
          onClick={e => e.stopPropagation()}
          title={mandate.gp_firm_name ? `Visit ${mandate.gp_firm_name}` : 'Visit website'}
          style={{
            position: 'absolute',
            bottom: 14,
            right: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 26,
            height: 26,
            borderRadius: 6,
            border: '1px solid var(--border)',
            color: 'var(--text-secondary)',
          }}
        >
          <ExternalLinkIcon />
        </a>
      )}
    </div>
  )
}

// Tiles keep the same shape at any window width: each column stays between
// 400 and 440px, and a wider window adds columns rather than stretching
// tiles (with 1fr columns, narrowing the window squeezed tiles tall and thin,
// and widening it stretched them). min(100%, ...) lets a single tile shrink
// to fit a phone-width screen.
function MandateGrid({ mandates }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 400px), 440px))', gap: 16 }}>
      {mandates.map(m => <MandateTile key={m.mandate_id} mandate={m} />)}
    </div>
  )
}

// Live mandates first; a partner's closed or on-hold ones follow, marked.
const liveFirst = items => [...items].sort((a, b) => (a.bd_stage === 'LIVE' ? 0 : 1) - (b.bd_stage === 'LIVE' ? 0 : 1))

export function BranchedGrid({ mandates }) {
  return <BranchSections mandates={mandates} renderGrid={items => <MandateGrid mandates={liveFirst(items)} />} />
}

const STAGE_NOTE = { CLOSED: 'Closed', 'ON HOLD': 'On hold' }

export default function PortalMandates() {
  const { profile } = useAuth()
  const { data: mandates, isLoading } = useQuery({
    queryKey: ['portalMandates'],
    queryFn: () => api.get('/api/portal/mandates').then(r => r.data.data),
  })

  const engaged    = (mandates || []).filter(m => m.engaged)
  const notEngaged = (mandates || []).filter(m => !m.engaged)
  const yourMandatesHeading = profile?.name ? `${profile.name}'s Mandates` : 'Your Mandates'

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-text" style={{ flex: 1 }}>
          <h1 style={PORTAL_PAGE_TITLE_STYLE}>Mandates</h1>
        </div>
        <PortalSearch />
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="spinner spinner-lg" />
        </div>
      ) : !mandates?.length ? (
        <div className="empty-state">
          <h3>No active mandates</h3>
          <p>Check back soon.</p>
        </div>
      ) : (
        <>
          {engaged.length > 0 && (
            <div style={{ marginBottom: 32 }}>
              <h2 style={SECTION_HEADING_STYLE}>{yourMandatesHeading}</h2>
              <BranchedGrid mandates={engaged} />
            </div>
          )}

          {notEngaged.length > 0 && (
            <div>
              <h2 style={SECTION_HEADING_STYLE}>
                {engaged.length > 0 ? 'Other RGA Active Mandates' : 'Active Mandates'}
              </h2>
              <BranchedGrid mandates={notEngaged} />
            </div>
          )}

        </>
      )}
    </div>
  )
}
